import { useCallback, useRef, useState, useMemo } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import classnames from 'classnames';

import { useExplorationsQuery } from '@/generated/graphql';
import { useBuildAccountUrl, useSelectedAccount } from '@/lib/accounts/account-context';
import { useScreenSize, Breakpoint } from '@/lib/hooks/use-screen-size';
import { useKeyPress } from '@/lib/hooks/use-key-press';
import { useTrackEvent } from '@/lib/analytics/analytics-context';
import { IconButton, InlineButton } from '@/components/button';
import { Tooltip } from '@/components/tooltip';
import { convertModelTypes } from '@/explore/input';
import { useResizeHandle } from '@/lib/hooks/use-resize-handle';
import { showChatWidget } from '@/lib/utils/support';
import { getModelSections } from '@/core/model';
import {
  getExplorationListFolders,
  getExplorationFolder,
  explorationHasFolderPrefix,
} from '@/core/exploration';

import { buildModelExploration } from '@/core/exploration/builder';
import { hasExplorationSection } from '@/core/exploration';
import { notNil } from '@/lib/utils';

import { getTheme } from '@/lib/theme';

import { AccountMenu } from '../header/account-menu';
import { Dropdown, DropdownMenuItem, DropdownIconSizePx } from '../dropdown';
import { Icon } from '../icon';
import { useLayoutContext } from './layout-context';
import { NavItem } from './nav-item';

import styles, { sidebarMinWidth, sidebarMaxWidth } from './sidebar-layout.module.scss';

const SidebarMinWidth = parseInt(sidebarMinWidth);
const SidebarMaxWidth = parseInt(sidebarMaxWidth);

const Logo = () => (
  <img
    src={getTheme() === 'light' ? '/logo-pinguin-bordered.svg' : '/logo-pinguin.svg'}
    style={{ width: 24 }}
    title="Supersimple"
    alt="Supersimple logo"
  />
);

interface SidebarSection {
  section: string;
  children: React.ReactNode[];
}

const SidebarSection = ({ section, children }: SidebarSection) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  return (
    <>
      <InlineButton
        className={styles.subsectionTitle}
        onClick={() => setIsCollapsed((val) => !val)}>
        {section}
        {isCollapsed ? <Icon name="ChevronDown" size={12} /> : <Icon name="ChevronUp" size={12} />}
      </InlineButton>
      {!isCollapsed && (
        <div className={classnames(styles.sidebarListings, styles.sectionedListings)}>
          {children}
        </div>
      )}
    </>
  );
};

export const Sidebar = () => {
  const navigate = useNavigate();
  const {
    isLeftSidebarOpen,
    isRightSidebarOpen,
    setIsLeftSidebarOpen,
    setIsRightSidebarOpen,
    activeExplorationSourceId,
    setActiveExplorationSourceId,
    toggleLeftSidebar,
  } = useLayoutContext();
  const trackEvent = useTrackEvent();

  useKeyPress('z', () => {
    const isEitherSidebarOpen = isLeftSidebarOpen || isRightSidebarOpen;
    if (isEitherSidebarOpen) {
      setIsLeftSidebarOpen(false);
      setIsRightSidebarOpen(false);
    } else {
      setIsLeftSidebarOpen(true);
      setIsRightSidebarOpen(true);
    }
  });

  useKeyPress(
    ['b', ['ctrl', 'shift'], ['meta', 'shift']],
    () => {
      setIsLeftSidebarOpen(!isLeftSidebarOpen);
    },
    { includeInputs: true },
  );

  const account = useSelectedAccount();
  const buildAccountUrl = useBuildAccountUrl();
  const screenSize = useScreenSize();

  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { sidebarWidth, isResizing } = useResizeHandle<HTMLDivElement, HTMLDivElement>(
    resizeHandleRef,
    sidebarRef,
    (target) => target.offsetWidth,
    (size, { dx }) => size + dx,
    (target, size) => (target.style.width = `${size}px`),
    { minSize: SidebarMinWidth, maxSize: SidebarMaxWidth },
  );

  const { data: explorations, loading } = useExplorationsQuery({
    variables: { accountId: account.accountId },
    fetchPolicy: 'cache-first',
  });

  const models = useMemo(() => {
    if (explorations?.account?.models === undefined) {
      return [];
    }
    return convertModelTypes(explorations.account.models) ?? [];
  }, [explorations]);

  const savedExplorations = useMemo(
    () => explorations?.account?.explorations ?? [],
    [explorations],
  );
  const explorationSections = useMemo(
    () => getExplorationListFolders(savedExplorations),
    [savedExplorations],
  );
  const ungroupedExplorations = savedExplorations.filter(
    (item) => !explorationHasFolderPrefix(item),
  );

  const modelSections = useMemo(() => getModelSections(models), [models]);
  const modelExplorations = useMemo(
    () => models.map((model) => buildModelExploration(model)),
    [models],
  );
  const modelExplorationsWithoutSections = useMemo(
    () => modelExplorations.filter((exploration) => !hasExplorationSection(exploration)),
    [modelExplorations],
  );

  const accountPinnedExplorations = useMemo(
    () =>
      (account.pinnedExplorationIds ?? [])
        .map((id) => savedExplorations.find((exploration) => exploration.explorationId === id))
        .filter(notNil),
    [account, savedExplorations],
  );

  const userPinnedExplorations = useMemo(
    () =>
      (account.me?.pinnedExplorationIds ?? [])
        .map((id) => savedExplorations.find(({ explorationId }) => explorationId === id))
        .filter(notNil),
    [account, savedExplorations],
  );

  const handleMobileNavigation = useCallback(() => {
    if (screenSize.breakpoint <= Breakpoint.lg) {
      setIsLeftSidebarOpen(false);
    }
  }, [screenSize.breakpoint, setIsLeftSidebarOpen]);

  const handleNavClick = useCallback(() => {
    setActiveExplorationSourceId(null);
    handleMobileNavigation();
  }, [setActiveExplorationSourceId, handleMobileNavigation]);

  const onClickLogOut = () => navigate('/logout');

  const helpMenuItems: DropdownMenuItem[] = [
    {
      type: 'link',
      label: 'Account Settings',
      icon: <Icon name="Settings" size={DropdownIconSizePx} />,
      href: buildAccountUrl('/settings/general'),
    },
    { type: 'divider' },
    {
      type: 'button',
      label: 'Get Help',
      icon: <Icon name="MessageSquare" size={DropdownIconSizePx} />,
      className: 'intercom-launcher',
      onClick: () => {
        showChatWidget();
        handleMobileNavigation();
      },
    },
    {
      type: 'link',
      label: 'Documentation',
      icon: <Icon name="Edit2" size={DropdownIconSizePx} />,
      href: 'https://docs.supersimple.io',
      external: true,
    },
    { type: 'divider' },
    {
      type: 'button',
      label: 'Log out',
      icon: <Icon name="X" size={DropdownIconSizePx} />,
      onClick: onClickLogOut,
    },
  ];

  return (
    <aside
      className={classnames(styles.leftSidebar, {
        [styles.collapsedSidebar]: !isLeftSidebarOpen,
        [styles.reducedMotion]: isResizing,
      })}
      style={{ width: sidebarWidth !== null ? `${sidebarWidth}px` : undefined }}
      ref={sidebarRef}>
      {!isLeftSidebarOpen ? (
        <Tooltip content="Expand sidebar">
          <IconButton
            icon="ChevronRight"
            size="small"
            className={styles.expandBtn}
            onClick={() => {
              toggleLeftSidebar(true);
              trackEvent('Left Sidebar Opened');
            }}
          />
        </Tooltip>
      ) : (
        <>
          <div className={styles.resizeHandle} ref={resizeHandleRef} />
          <div className={styles.topRow}>
            <NavLink
              to={buildAccountUrl('/explore')}
              style={{ textDecoration: 'none', color: 'inherit' }}>
              <Logo />
            </NavLink>

            <Tooltip content="Collapse sidebar">
              <IconButton
                icon="ChevronLeft"
                size="small"
                className={styles.collapseBtn}
                onClick={() => {
                  toggleLeftSidebar(false);
                  trackEvent('Left Sidebar Closed');
                }}
              />
            </Tooltip>
          </div>

          <AccountMenu />

          <div className={styles.sidebarContent}>
            <div className={styles.sidebarListings}>
              <NavLink
                end
                to={buildAccountUrl('/explore')}
                className={({ isActive }) =>
                  classnames(styles.sidebarListing, {
                    [styles.active]: isActive,
                  })
                }
                onClick={handleNavClick}>
                Explore
              </NavLink>
            </div>

            {(userPinnedExplorations.length > 0 || accountPinnedExplorations.length > 0) && (
              <>
                <div className={styles.sectionTitle}>
                  <Icon name="Bookmark" size={16} />
                  Pinned
                </div>

                {userPinnedExplorations.length > 0 && accountPinnedExplorations.length > 0 && (
                  <div className={styles.subSectionSubTitle}>My pins</div>
                )}
                {userPinnedExplorations.length > 0 && (
                  <div className={styles.sidebarListings}>
                    {userPinnedExplorations.map((item) => (
                      <NavItem
                        key={item.explorationId}
                        item={item}
                        isSavedExploration
                        activeExplorationSourceId={activeExplorationSourceId}
                        buildAccountUrl={buildAccountUrl}
                        onNavClick={handleNavClick}
                      />
                    ))}
                  </div>
                )}
                {userPinnedExplorations.length > 0 && accountPinnedExplorations.length > 0 && (
                  <div className={styles.subSectionSubTitle}>Team pins</div>
                )}
                {accountPinnedExplorations.length > 0 && (
                  <div className={styles.sidebarListings}>
                    {accountPinnedExplorations.map((item) => (
                      <NavItem
                        key={item.explorationId}
                        item={item}
                        isSavedExploration
                        activeExplorationSourceId={activeExplorationSourceId}
                        buildAccountUrl={buildAccountUrl}
                        onNavClick={handleNavClick}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {savedExplorations.length > 0 && (
              <>
                <div className={styles.sectionTitle}>
                  <Icon name="Exploration" size={16} />
                  Explorations
                </div>
                <div className={styles.sidebarListings}>
                  {ungroupedExplorations.map((item) => (
                    <NavItem
                      key={item.explorationId}
                      item={item}
                      isSavedExploration
                      activeExplorationSourceId={activeExplorationSourceId}
                      buildAccountUrl={buildAccountUrl}
                      onNavClick={handleNavClick}
                    />
                  ))}
                </div>
                {explorationSections.map((section) => (
                  <SidebarSection key={section} section={section}>
                    {savedExplorations
                      .filter((item) => getExplorationFolder(item.name) === section)
                      .map((item) => (
                        <NavItem
                          key={item.explorationId}
                          item={item}
                          isSavedExploration
                          activeExplorationSourceId={activeExplorationSourceId}
                          buildAccountUrl={buildAccountUrl}
                          onNavClick={handleNavClick}
                        />
                      ))}
                  </SidebarSection>
                ))}
              </>
            )}

            <div className={styles.sectionTitle}>
              <Icon name="Model" size={16} />
              Models
            </div>
            {modelSections.map((section) => (
              <SidebarSection key={section} section={section}>
                {modelExplorations
                  .filter(({ labels }) => labels.section === section)
                  .map((item) => (
                    <NavItem
                      key={item.explorationId}
                      item={item}
                      activeExplorationSourceId={activeExplorationSourceId}
                      buildAccountUrl={buildAccountUrl}
                      onNavClick={handleNavClick}
                    />
                  ))}
              </SidebarSection>
            ))}
            {modelExplorationsWithoutSections.length > 0 && (
              <>
                {modelSections.length > 0 ? (
                  <SidebarSection key="other-models" section="Other Data Models">
                    {modelExplorationsWithoutSections.map((item) => (
                      <NavItem
                        key={`model-${item.explorationId}`}
                        item={item}
                        activeExplorationSourceId={activeExplorationSourceId}
                        buildAccountUrl={buildAccountUrl}
                        onNavClick={handleNavClick}
                      />
                    ))}
                  </SidebarSection>
                ) : (
                  <div className={styles.sidebarListings}>
                    {modelExplorationsWithoutSections.map((item) => (
                      <NavItem
                        key={`model-${item.explorationId}`}
                        item={item}
                        activeExplorationSourceId={activeExplorationSourceId}
                        buildAccountUrl={buildAccountUrl}
                        onNavClick={handleNavClick}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {!loading && models.length === 0 && (
            <p className={styles.empty}>
              <>
                <Link
                  to="https://docs.supersimple.io/guides/setup/#data-model-setup"
                  target="_blank">
                  Create your first data models
                </Link>
              </>
            </p>
          )}

          <footer className={styles.sidebarFooter}>
            <NavLink
              end
              to={buildAccountUrl('/history')}
              className={({ isActive }) =>
                classnames(styles.footerBtn, {
                  [styles.active]: isActive,
                })
              }
              onClick={handleNavClick}>
              <Icon name="Book" size={16} />
              History
            </NavLink>
            <Dropdown
              items={helpMenuItems}
              align="left"
              className={styles.sidebarListing}
              trigger={(isOpen, setIsOpen) => (
                <button className={styles.footerBtn} onClick={() => setIsOpen(!isOpen)}>
                  <Icon name="Settings" size={16} />
                  Settings
                </button>
              )}
            />
          </footer>
        </>
      )}
    </aside>
  );
};
